<template>
  <section class="h-full mx-4">
    <BaseTable
      :headers="assortmentsHeaders"
      :list="assortments"
      clickable
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewAssortment"
      @refresh-data="refreshData"
      @table-item-clicked="onAssortmentClick"
      table-id="assortments"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            :icon="IconPencilSquare"
            @action="onEditAssortment(item as Assortment)" />
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as Assortment)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>

            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <AssortmentsModalCreateEdit
      v-if="createEditModalActive && dropdownTraderGroupItemNames"
      :assortment="assortment"
      :mode="createEditModalMode"
      :trader-group-item-names="dropdownTraderGroupItemNames"
      @close-modal="createEditModalActive = false"
      @save="onSave">
    </AssortmentsModalCreateEdit>

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()">
      <template #text>{{
        $t('messages.info.deleteSingle', { 'item': deleteRowItem?.traderGroupItemNamePath })
      }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import {
  removeAssortmentsByIds,
  getAssortments,
  addOrEditAssortment,
} from '@/services/api/materials';
import { v4 as uuidv4 } from 'uuid';

import BaseTable from '@/components/common/BaseTable.vue';
import AssortmentsModalCreateEdit from '@/components/assortmentsManagement/AssortmentsModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconPencilSquare from '@/assets/icons/IconPencilSquare.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type Assortment } from '@/types/Materials';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });
const { dropdownTraderGroupItemNames } = storeToRefs(useLookupsStore());
const { getTraderGroupItemNames } = useLookupsStore();

const router = useRouter();

const assortments = ref<Assortment[]>([]);
const filterIsActive = ref<boolean | undefined>(undefined);

const assortmentsHeaders = [
  {
    value: 'traderGroupItemNamePath',
    text: $t('questionnaire.traderSiteGroup'),
    type: 'text',
    active: true,
  },
  {
    value: 'questionsType',
    text: $t('questionnaire.questionnaireType'),
    type: 'text',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeAssortment(): Assortment {
  return {
    id: uuidv4(),
    bindingType: 1,
    isFromManager: true,
    isActive: true,
    traderGroupItemID: '',
    traderGroupItemNamePath: '',
  };
}

async function loadData() {
  const res = await getAssortments();
  assortments.value = populateAssortment(res);
}

onMounted(async () => {
  await loadData();
  await getTraderGroupItemNames();
});

function getQuestionsType(bindingType: number): string {
  return bindingType == 1 ? 'Material groups' : 'Materials';
}

function populateAssortment(assortments: Assortment[]): Assortment[] {
  return assortments.map((type) => ({
    ...type,
    questionsType: type.bindingType ? getQuestionsType(type.bindingType) : undefined,
  }));
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: Assortment[]) {
  const idsToDelete = items.filter((item) => item.isFromManager).map((item) => item.id);
  const res = await removeAssortmentsByIds(idsToDelete);
  if (!res && assortments.value)
    assortments.value = assortments.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeAssortmentsByIds([idToDelete]);
  if (!res && assortments.value)
    assortments.value = assortments.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const assortment = ref<Assortment>(initializeAssortment());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNewAssortment() {
  assortment.value = initializeAssortment();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditAssortment(item: Assortment) {
  assortment.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: Assortment) {
  const res = await addOrEditAssortment(modifiedData);
  if (res) {
    toastStore.success({ text: `${$t('common.toast.success')}` });
    createEditModalActive.value = false;
    await loadData();
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
  }
}

async function refreshData(data: TableFilterData) {
  await loadData();

  if (data.query) {
    assortments.value = filterData(assortments.value, data.query, [
      'traderGroupItemNamePath',
      'questionsType',
    ]);
  }

  if (data.column !== undefined && data.order && assortments.value) {
    assortments.value = sortData(assortments.value, data.column, data.order, assortmentsHeaders);
  }

  if (data.isActive !== undefined)
    assortments.value = filterActive(assortments.value, data.isActive);
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<Assortment>();
function onDeleteRow(item: Assortment) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onAssortmentClick(item: Assortment) {
  router.push({ name: 'assortment-details', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
